/**
 * @file app.routes.ts
 * @description Application route definitions. All components are lazy-loaded.
 *
 * Homepage: `/` serves the marketing landing page (unguarded, indexable).
 * Auth pages (login, signup, verify, reset-password) are unguarded.
 * All other routes require authentication via authGuard.
 *
 * Each route declares `data.seo` consumed by `SeoService` in app.ts to set
 * per-route title, description, canonical URL, and robots directive.
 *
 * Two navigations share this file (`navV2` in the environment, spec 2026-10-23): today's
 * Reports and References, and the five areas Read, Write, Search, Library and Support.
 * `appRoutes(env)` builds one set at start-up; Angular refuses a guard on a redirect, so the
 * choice cannot be made per route. Under the five areas the old paths stay as relative
 * redirects, which keep their parameters, query string and fragment.
 */
import { Route, Routes } from '@angular/router';
import type { SeoRouteData } from '@kirigami/platform/browser';
import { authGuard } from './guards/auth.guard';
import { adminGuard } from './guards/admin.guard';
import { LandingComponent } from './components/landing/landing.component';
import { navV2Enabled, readAvailable } from './components/shared/app-paths';
import { environment } from '../environments/environment';
import type { Environment } from '../environments/environment.model';
import type { AppRouteData } from './route-data';

/** Default "no-index" SEO data for authenticated / transactional pages. */
const SEO_NOINDEX: SeoRouteData = { index: false };

const PUBLIC_ROUTES: Routes = [
  // ── Homepage (unguarded) ──────────────────────────
  {
    path: '',
    pathMatch: 'full',
    // Eager, unlike every other route: this is the default entry page, and
    // lazy-loading it puts an extra chunk round trip on the LCP critical path.
    component: LandingComponent,
    data: {
      seo: {
        index: true,
        title: 'Kirigami | Scientific Writing Guidance for Researchers',
        description:
          'Kirigami guides your scientific writing. It flags problems, explains why they matter and answers questions from your own library. You stay the author.',
      } satisfies SeoRouteData,
    },
  },

  // Legacy redirect: `/landing` → `/` so old bookmarks and shared links
  // still land on the homepage instead of 404-ing.
  {
    path: 'landing',
    pathMatch: 'full',
    redirectTo: '',
  },

  {
    path: 'about',
    loadComponent: () => import('./components/about/about.component').then((m) => m.AboutComponent),
    data: {
      seo: {
        index: true,
        title: 'About us | Kirigami',
        description:
          'Why Kirigami exists, how it started, and the six principles every Kirigami tool follows, from human authorship to privacy. Researchers stay the authors of their work.',
      } satisfies SeoRouteData,
    },
  },

  // ── Auth pages (unguarded) ────────────────────────
  {
    path: 'login',
    loadComponent: () =>
      import('./components/auth/login/login.component').then((m) => m.LoginComponent),
    data: { seo: { ...SEO_NOINDEX, title: 'Sign in | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'signup',
    loadComponent: () =>
      import('./components/auth/signup/signup.component').then((m) => m.SignupComponent),
    data: { seo: { ...SEO_NOINDEX, title: 'Create account | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'verify',
    loadComponent: () =>
      import('./components/auth/verify-code/verify-code.component').then(
        (m) => m.VerifyCodeComponent,
      ),
    data: { seo: { ...SEO_NOINDEX, title: 'Verify email | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./components/auth/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
    data: { seo: { ...SEO_NOINDEX, title: 'Reset password | Kirigami' } satisfies SeoRouteData },
  },
];

const ACCOUNT_AND_ADMIN_ROUTES: Routes = [
  {
    path: 'pricing',
    loadComponent: () =>
      import('./components/upgrade/upgrade.component').then((m) => m.UpgradeComponent),
    data: {
      seo: {
        index: true,
        title: 'Pricing | Kirigami Scientific Writing Guidance',
        description:
          'Kirigami offers a free plan and two subscriptions. Credits are metered per analysis based on the length of each paper and section.',
      } satisfies SeoRouteData,
    },
  },
  // Backward-compatible redirect for the former /upgrade route.
  { path: 'upgrade', redirectTo: 'pricing', pathMatch: 'full' },
  {
    path: 'account',
    loadComponent: () =>
      import('./components/account/account.component').then((m) => m.AccountComponent),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Account | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'admin',
    loadComponent: () => import('./components/admin/admin.component').then((m) => m.AdminComponent),
    canActivate: [authGuard, adminGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Admin | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'admin/users/:id',
    loadComponent: () =>
      import('./components/admin/user-profile/user-profile.component').then(
        (m) => m.UserProfileComponent,
      ),
    canActivate: [authGuard, adminGuard],
    data: {
      seo: { ...SEO_NOINDEX, title: 'User profile | Admin | Kirigami' } satisfies SeoRouteData,
    },
  },
  {
    path: 'admin/support',
    loadComponent: () =>
      import('./components/admin/support/admin-support.component').then(
        (m) => m.AdminSupportComponent,
      ),
    canActivate: [authGuard, adminGuard],
    data: {
      seo: { ...SEO_NOINDEX, title: 'Support inbox | Admin | Kirigami' } satisfies SeoRouteData,
    },
  },
  {
    path: 'admin/analytics',
    loadComponent: () =>
      import('./components/admin/analytics/analytics.component').then((m) => m.AnalyticsComponent),
    canActivate: [authGuard, adminGuard],
    data: {
      seo: { ...SEO_NOINDEX, title: 'Analytics | Admin | Kirigami' } satisfies SeoRouteData,
    },
  },
  {
    path: 'admin/pilot-subscriptions',
    loadComponent: () =>
      import('./components/admin/pilot-subscriptions/pilot-subscriptions.component').then(
        (m) => m.PilotSubscriptionsComponent,
      ),
    canActivate: [authGuard, adminGuard],
    data: {
      seo: {
        ...SEO_NOINDEX,
        title: 'Pilot subscriptions | Admin | Kirigami',
      } satisfies SeoRouteData,
    },
  },
  {
    path: 'admin/pilot-subscriptions/:id',
    loadComponent: () =>
      import('./components/admin/pilot-subscriptions/detail/pilot-subscription-detail.component').then(
        (m) => m.PilotSubscriptionDetailComponent,
      ),
    canActivate: [authGuard, adminGuard],
    data: {
      seo: {
        ...SEO_NOINDEX,
        title: 'Pilot subscription | Admin | Kirigami',
      } satisfies SeoRouteData,
    },
  },
  {
    path: 'manage-subscriptions',
    loadComponent: () =>
      import('./components/subscriptions/subscriptions.component').then(
        (m) => m.SubscriptionsComponent,
      ),
    canActivate: [authGuard],
    data: {
      seo: { ...SEO_NOINDEX, title: 'Manage subscriptions | Kirigami' } satisfies SeoRouteData,
    },
  },
  {
    path: 'lab-invitation/:subscriptionId',
    loadComponent: () =>
      import('./components/lab-invitation/lab-invitation.component').then(
        (m) => m.LabInvitationComponent,
      ),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Lab invitation | Kirigami' } satisfies SeoRouteData },
  },
];

// ── Wildcard: unknown URLs render a real 404 page (noindex) ──
const NOT_FOUND_ROUTE: Route = {
  path: '**',
  loadComponent: () =>
    import('./components/not-found/not-found.component').then((m) => m.NotFoundComponent),
  data: { seo: { ...SEO_NOINDEX, title: 'Page not found | Kirigami' } satisfies SeoRouteData },
};

/** Today's Reports and References. */
const V1_AREA_ROUTES: Routes = [
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./components/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Dashboard | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'report/:id/summary',
    loadComponent: () =>
      import('./components/report-summary/report-summary.component').then(
        (m) => m.ReportSummaryComponent,
      ),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Report summary | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'report/:id',
    loadComponent: () =>
      import('./components/report-detail/report-detail.component').then(
        (m) => m.ReportDetailComponent,
      ),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Report | Kirigami' } satisfies SeoRouteData },
  },
  // ── References (search-first home + KB management) ─
  {
    path: 'references',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./components/references/references.routes').then((m) => m.REFERENCES_ROUTES),
  },

  // ── Knowledge Bases + Search Engines ──────────────
  {
    path: 'knowledge-bases',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./components/knowledge-bases/knowledge-bases.routes').then(
        (m) => m.KNOWLEDGE_BASES_ROUTES,
      ),
  },
];

/** Read, Write, Search and Library, with the old paths redirecting to them. */
const V2_AREA_ROUTES: Routes = [
  // ── The five areas (navV2) ────────────────────────
  // Old paths first: relative redirects keep the parameters and the query string, so
  // emails, bell entries and bookmarks (`/dashboard?welcome=free`, `/report/:id?section=`)
  // land where they meant to.
  { path: 'dashboard', pathMatch: 'full', redirectTo: 'write' },
  { path: 'report/:id/summary', pathMatch: 'full', redirectTo: 'write/:id/summary' },
  { path: 'report/:id', pathMatch: 'full', redirectTo: 'write/:id' },
  { path: 'references', pathMatch: 'full', redirectTo: 'search' },
  { path: 'references/manage', pathMatch: 'full', redirectTo: 'library' },
  { path: 'references/manage/:id', pathMatch: 'full', redirectTo: 'library/:id' },
  { path: 'knowledge-bases', pathMatch: 'full', redirectTo: 'library' },
  { path: 'knowledge-bases/:id', pathMatch: 'full', redirectTo: 'library/:id' },
  {
    path: 'write',
    loadComponent: () =>
      import('./components/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Write | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'write/:id/summary',
    loadComponent: () =>
      import('./components/report-summary/report-summary.component').then(
        (m) => m.ReportSummaryComponent,
      ),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Summary | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'write/:id',
    loadComponent: () =>
      import('./components/report-detail/report-detail.component').then(
        (m) => m.ReportDetailComponent,
      ),
    canActivate: [authGuard],
    data: { seo: { ...SEO_NOINDEX, title: 'Manuscript | Kirigami' } satisfies SeoRouteData },
  },
  {
    path: 'search',
    // Search v2; the old navigation keeps ReferencesHomeComponent on /references.
    loadComponent: () =>
      import('./components/search/search-page.component').then((m) => m.SearchPageComponent),
    canActivate: [authGuard],
    data: {
      seo: { ...SEO_NOINDEX, title: 'Search | Kirigami' } satisfies SeoRouteData,
      // Switching tabs writes `?tab=` into the URL; that is not a new page.
      samePage: 'search',
    } satisfies AppRouteData,
  },
  {
    path: 'library',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./components/references/library.routes').then((m) => m.LIBRARY_ROUTES),
  },
];

/** Support, in both navigations: `/support`, with the old `/support-center` redirecting to it. */
const SUPPORT_ROUTES: Routes = [
  {
    path: 'support',
    loadComponent: () =>
      import('./components/support-center/support-center.component').then(
        (m) => m.SupportCenterComponent,
      ),
    data: {
      seo: {
        index: true,
        canonicalPath: '/support',
        title: 'Support | Kirigami',
        description:
          'How Kirigami helps you read papers, write and revise your manuscript, search the literature and organise your knowledge bases, and how your work is kept private.',
      } satisfies SeoRouteData,
    },
  },
  { path: 'support-center', pathMatch: 'full', redirectTo: 'support' },
];

/** Read lists the papers the reference manager knows, so it exists only where that service does. */
const READ_ROUTE: Route = {
  path: 'read',
  loadComponent: () => import('./components/read/read.component').then((m) => m.ReadComponent),
  canActivate: [authGuard],
  data: {
    seo: { ...SEO_NOINDEX, title: 'Read | Kirigami' } satisfies SeoRouteData,
    // `?paper=` and `?kb=` change what the page shows, not the page.
    samePage: 'read',
  } satisfies AppRouteData,
};

export function appRoutes(env: Environment): Routes {
  return [
    ...PUBLIC_ROUTES,
    ...(readAvailable(env) ? [READ_ROUTE] : []),
    ...(navV2Enabled(env) ? V2_AREA_ROUTES : V1_AREA_ROUTES),
    ...SUPPORT_ROUTES,
    ...ACCOUNT_AND_ADMIN_ROUTES,
    NOT_FOUND_ROUTE,
  ];
}

/** The set for this environment, resolved by hostname when the module loads. */
export const routes: Routes = appRoutes(environment);
